import React from 'react';
import {
  CloudArrowUpIcon,
  ChartBarSquareIcon,
  Squares2X2Icon,
  ChatBubbleLeftRightIcon,
  WrenchScrewdriverIcon,
  BoltIcon,
  CircleStackIcon,
  DocumentMagnifyingGlassIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import SectionSurface from '../shared/SectionSurface';

const BUILD_GROUPS = [
  {
    label: 'Snowflake & platform engineering',
    accent: 'bg-accent-50/40 text-accent-700',
    borderAccent: 'border-l-accent-400',
    iconBg: 'bg-accent-50 text-accent-700',
    items: [
      {
        icon: WrenchScrewdriverIcon,
        title: 'Migration & Platform Engineering',
        body: 'Move legacy warehouses, fragmented reporting systems, and data pipelines into Snowflake-ready enterprise architecture.',
      },
      {
        icon: CloudArrowUpIcon,
        title: 'Snowflake Enterprise AI',
        body: 'Build Snowflake-native platforms for semantic models, Cortex, AI agents, and intelligent applications.',
      },
    ],
  },
  {
    label: 'Semantic foundation',
    accent: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
    borderAccent: 'border-l-brand-900',
    iconBg: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
    items: [
      {
        icon: Squares2X2Icon,
        title: 'Semantic Layers',
        body: 'Define metrics, business concepts, and domain logic once so every dashboard, app, and AI agent speaks the same language.',
      },
      {
        icon: ChatBubbleBottomCenterTextIcon,
        title: 'Cortex Analyst for Talk-to-Data',
        body: 'Plain-language questions on certified metrics and semantic models, Cortex Analyst maps intent to governed SQL on Snowflake, aligned with your metric catalog and business glossary.',
        featured: true,
      },
      {
        icon: DocumentMagnifyingGlassIcon,
        title: 'Cortex Search for Unstructured Knowledge',
        body: 'Make PDFs, policies, contracts, handbooks, tickets, and procedures searchable and usable by AI agents, governed enterprise memory from document repositories.',
      },
    ],
  },
  {
    label: 'Intelligent applications',
    accent: 'bg-brand-50/40 text-brand-700',
    borderAccent: 'border-l-brand-500',
    iconBg: 'bg-brand-50 text-brand-600',
    columns: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    items: [
      {
        icon: ChartBarSquareIcon,
        title: 'BI Modernization',
        body: 'Modernize Power BI, Tableau, QuickSight, and legacy dashboards into governed, searchable analytics experiences.',
      },
      {
        icon: ChatBubbleLeftRightIcon,
        title: 'Conversational Analytics',
        body: 'Create talk-to-data experiences using governed semantic models, Cortex, embedded BI, and AI copilots.',
      },
      {
        icon: BoltIcon,
        title: 'Decision Intelligence Agents',
        body: 'Build agents that monitor KPIs, detect anomalies, trigger alerts, and recommend next actions.',
      },
    ],
  },
];

const FEATURED_PLATFORM = {
  icon: CircleStackIcon,
  title: 'Unified Data + Application Platform',
  body:
    'Collapse fragmented transactional systems, analytical databases, BI layers, and document repositories into a Snowflake-centered intelligence ecosystem, connected through Snowflake, Postgres, Java services, React interfaces, and Streamlit apps.',
};

function BuildCard({ icon: Icon, title, body, iconBg, borderAccent, featured = false }) {
  if (featured) {
    return (
      <article className={`rounded-xl border border-ink-200 bg-white p-6 sm:p-8 shadow-xs border-l-4 ${borderAccent}`}>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
          <div className="flex items-start gap-4 lg:max-w-xs shrink-0">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
              <Icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <h3 className="text-lg font-semibold text-ink-900 leading-snug pt-2">{title}</h3>
          </div>
          <p className="text-sm leading-7 text-ink-600 lg:pt-2">{body}</p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex gap-4 rounded-xl border border-ink-200 bg-white p-5 shadow-xs transition-all duration-200 hover:border-brand-200 hover:shadow-md">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-ink-900 leading-snug">{title}</h3>
        <p className="mt-2 text-sm leading-6 text-ink-600">{body}</p>
      </div>
    </article>
  );
}

export default function WhatWeBuild() {
  return (
    <SectionSurface variant="subtle" decor="grid" bleed="both" flowTop flowBottom overlap>
      <div className="container-x section-tight">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow">What We Build</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            From Business Intelligence to Semantic Intelligence
          </h2>
          <p className="mt-4 text-sm leading-6 text-ink-600 sm:text-base">
            MyOrgLab builds the semantic foundation for Snowflake-powered Enterprise AI across platforms your teams already use.
          </p>
        </div>

        <div className="mt-12 space-y-12 lg:space-y-14">
          {BUILD_GROUPS.map((group) => (
            <div key={group.label}>
              <p className={`mb-5 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${group.accent}`}>
                {group.label}
              </p>
              <div className={`grid gap-4 ${group.columns || 'grid-cols-1 md:grid-cols-2'}`}>
                {group.items.map((item) => (
                  <div key={item.title} className={item.featured ? 'md:col-span-2' : undefined}>
                    <BuildCard
                      {...item}
                      iconBg={group.iconBg}
                      borderAccent={group.borderAccent}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div>
            <p className="mb-5 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide bg-brand-50/40 text-brand-700">
              Platform unification
            </p>
            <BuildCard
              {...FEATURED_PLATFORM}
              iconBg="bg-brand-50 text-brand-600"
              borderAccent="border-l-brand-500"
              featured
            />
          </div>
        </div>
      </div>
    </SectionSurface>
  );
}
